import { useState, useRef } from 'react';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';

export function useMusicUpload(storage, currentUser, showToast) {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadedFilePath, setUploadedFilePath] = useState(null);
  const fileInputRef = useRef(null);

  /**
   * Cleans up a specific file from Firebase Storage.
   * Called when replacing a file or canceling an upload.
   */
  const cleanupUpload = async (filePath) => {
    if (!filePath) return;
    try {
      const fileRef = storageRef(storage, filePath);
      await deleteObject(fileRef);
      console.log(`Cleaned up file from Storage: ${filePath}`);
    } catch (error) {
      console.error("Error cleaning up storage file:", error.message);
    }
  };

  /**
   * Resets the hook state WITHOUT deleting files from storage.
   * Call this in your Dashboard AFTER the database write is successful.
   */
  const clearUploadState = () => {
    setUploadedFilePath(null);
    setFileUploaded(false);
    setUploadProgress(0);
    setUploadError(null);
  };

  /**
   * Resets state AND deletes the uploaded file from storage.
   * Call this if the user cancels the form or closes the modal mid-process.
   */
  const resetUpload = async () => {
    if (uploadedFilePath) {
      await cleanupUpload(uploadedFilePath);
    }
    setUploadProgress(0);
    setFileUploaded(false);
    setUploadError(null);
    setUploadedFilePath(null);
  };

  const handleFileUpload = async (file, musicForm, setMusicForm) => {
    if (!file) return;

    // Validation
    const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/wave', 'audio/x-wav'];
    const allowedExtensions = ['.mp3', '.wav'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      const errorMsg = 'Only MP3 and WAV files are allowed';
      showToast(errorMsg, 'error');
      setUploadError(errorMsg);
      return;
    }

    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      const errorMsg = 'File size must be less than 100MB';
      showToast(errorMsg, 'error');
      setUploadError(errorMsg);
      return;
    }

    // If a file was already uploaded in this session, delete it first to save space
    if (uploadedFilePath) {
      await cleanupUpload(uploadedFilePath);
    }

    setUploadProgress(0);
    setUploadError(null);

    try {
      // Filename construction: lowercaseTitle-currentUserUID.extension
      const sanitizedTitle = (musicForm.name || file.name.split('.')[0])
        .toLowerCase()
        .replace(/[^a-z0-9]/gi, '-'); // Replace non-alphanumeric with hyphens
      
      const extension = file.name.split('.').pop();
      const customFilename = `${sanitizedTitle}-${currentUser.uid}.${extension}`;
      
      const fullPath = `artistMusic/${currentUser.uid}/${customFilename}`;
      const storageReference = storageRef(storage, fullPath);

      // Start Resumable Upload
      const uploadTask = uploadBytesResumable(storageReference, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          console.error('Upload task error:', error);
          setUploadError(error.message);
          setUploadProgress(0);
          showToast(`Upload failed: ${error.message}`, 'error');
        },
        async () => {
          // Success: Get URL and update state
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          
          setMusicForm({ ...musicForm, url: downloadUrl });
          setUploadedFilePath(fullPath);
          setFileUploaded(true);
          setUploadError(null);
          showToast('Music file uploaded successfully!', 'success');
          
          // Reset bar visual but keep status
          setTimeout(() => setUploadProgress(0), 1000);
        }
      );
    } catch (err) {
      console.error('General catch error:', err);
      setUploadError(err.message);
    }
  };

  return {
    uploadProgress,
    fileUploaded,
    uploadError,
    uploadedFilePath,
    fileInputRef,
    handleFileUpload,
    cleanupUpload,
    resetUpload,
    clearUploadState,
    setUploadProgress,
    setFileUploaded,
    setUploadError
  };
}